import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '@onchainstudios/cryptoys-common-ui';
import styles from './ConfirmOrder.module.css';
import Header from '../../../components/Header';
import Footer from '../../../components/Footer';
import PaymentSteps from '../../../components/PaymentSteps';
import { useMakePurchase } from '../../../lib/cryptoys/drops/purchase';
import { useEffect, useState } from 'react';
import { formatCurrencyUSD, calculateAge } from '../../../utils';
import { genericErrorMessage } from '../../../lib/cryptoys/utils';
import TermsConditions from '../../../components/TermsConditions/TermsConditions';
import { PaymentElement } from '@stripe/react-stripe-js';
import ErrorMessageBlock from '../../../components/ErrorMessageBlock';
import { RECAPTCHA_ERROR_CODE } from '../../../constants';

function ConfirmOrder({
  user,
  recaptchaToken,
  dropData,
  reservationData,
  elements,
  stripe
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const makePurchase = useMakePurchase();
  const { state: depositState } = useLocation();
  const [subtotal, setSubtotal] = useState();
  const [fee, setFee] = useState();
  const [total, setTotal] = useState();
  const cardFee = 0.05;
  const isCreditCardPurchase =
    searchParams.get('isCreditCardPurchase') === 'true';

  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [isPaymentFormComplete, setIsPaymentFormComplete] = useState(false);
  const paymentErrorMessage =
    'Sorry, we couldn’t verify your payment information. Please try again.';

  useEffect(() => {
    const isMissingDepositInformation = isCreditCardPurchase && !depositState;
    // Redirect to start page if no reservation ID in url or if there is no deposit state on CC purchases
    if (!searchParams.get('reservationId') || isMissingDepositInformation) {
      navigate(
        { pathname: '/', search: '?' + searchParams },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dropData && reservationData) {
      const cubePrice = dropData.data.price * searchParams.get('numCubes');
      const cubeFee = isCreditCardPurchase ? cubePrice * cardFee : 0;
      setSubtotal(cubePrice);
      setFee(cubeFee);
      setTotal(cubePrice + cubeFee);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropData, reservationData]);

  useEffect(() => {
    document.title = 'Cryptoys - Review Your Order Summary';
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    function handleUnload(e) {
      if (isPaymentLoading) {
        e.preventDefault();
        return (e.returnValue =
          'Your payment is in progress. Are you sure you would like to leave the page?');
      }
    }
    window.addEventListener('beforeunload', handleUnload);

    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [isPaymentLoading]);

  const purchase = async () => {
    const {
      id: dropId,
      name: dropName,
      legionId,
      price: unitPrice
    } = dropData.data;
    const { email, username, id: userId, dob, termsAcceptanceDate } = user;
    const metadata = {
      email,
      username,
      userId,
      calculatedAge: calculateAge(dob),
      accountCreationDate: new Date(termsAcceptanceDate),
      reservationId: searchParams.get('reservationId'),
      correlationId: searchParams.get('correlationId'),
      dropId,
      dropName,
      legionId,
      unitPrice,
      quantity: searchParams.get('numCubes'),
      subtotal,
      fee,
      total,
      currency: searchParams.get('purchaseCurrency1')
    };
    try {
      const updateData = isCreditCardPurchase
        ? {
            purchaseQuantity: searchParams.get('numCubes') // for Stripe credit card purchases, passing currency will return an error
          }
        : {
            purchaseQuantity: searchParams.get('numCubes'),
            purchaseCurrency1: searchParams.get('purchaseCurrency1')
          };
      const data = await makePurchase.mutateAsync({
        recaptchaToken,
        correlationId: searchParams.get('correlationId'),
        reservationId: searchParams.get('reservationId'),
        updateData
      });
      if (data) {
        window?.heap?.track('Purchase - Success', metadata);
        navigate({
          pathname: '/success',
          search: `?numCubes=${searchParams.get('numCubes')}`
        });
      }
    } catch (error) {
      let errorMessage = error.message;
      window?.heap?.track('Purchase - Fail', {
        ...metadata,
        errorMessage
      });
      if (errorMessage === genericErrorMessage) {
        errorMessage = paymentErrorMessage;
      }
      if (isCreditCardPurchase) {
        // show fulfillment error page
        searchParams.set('error', encodeURIComponent(errorMessage));
        navigate({
          pathname: '/purchase-error',
          search: '?' + searchParams
        });
      } else {
        if (error.code === RECAPTCHA_ERROR_CODE) {
          navigate({
            pathname: '/verification-error',
            search: '?' + searchParams
          });
        } else {
          // redirect to crypto selection page
          searchParams.set('error', encodeURIComponent(errorMessage));
          navigate({
            pathname: '/select-currency',
            search: '?' + searchParams
          });
        }
      }
    }
  };

  const handleStripeError = (errorMessage) => {
    searchParams.set('error', errorMessage);
    setSearchParams(searchParams.toString(), { replace: true });
    setIsPaymentLoading(false);
  };

  const confirmPayment = async () => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    const result = await stripe.confirmPayment({
      elements,
      redirect: 'if_required'
    });

    if (result.error) {
      handleStripeError(result.error.message);
    } else {
      if (result.paymentIntent.status === 'succeeded') {
        purchase();
      } else if (result.paymentIntent.status === 'requires_payment_method') {
        handleStripeError('Payment failed. Please try another payment method.');
      }
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setIsPaymentLoading(true);
    searchParams.delete('error');
    if (isCreditCardPurchase) {
      if (stripe) {
        confirmPayment();
      }
    } else {
      purchase();
    }
  };

  return (
    <>
      <div className="container">
        <Header
          isPurchaseInProgress={isPaymentLoading || makePurchase.isLoading}
        />
        <main
          className={`${
            isCreditCardPurchase ? styles.isCreditCardPurchase : ''
          } mainContainer`}
        >
          <form
            id="complete-transaction-form"
            data-testid="complete-transaction-form"
            onSubmit={handleSubmit}
          >
            <PaymentSteps />
            <h1 className="heading2">Review Your Order Summary</h1>
            {!!searchParams.get('error') && (
              <>
                <ErrorMessageBlock
                  message={decodeURIComponent(searchParams.get('error'))}
                />
                <br />
              </>
            )}
            <div className={styles.payAndConfirmForm}>
              <div
                id="transaction-summary-section"
                data-testid="transaction-summary-section"
                className={`${styles.orderSummary} borderMask`}
              >
                <div className={styles.purchaseSummary}>
                  <div className={styles.summaryGroup}>
                    <p
                      className={`bodyMicroReg ${styles.purchaseSummaryLabel}`}
                    >
                      QUANTITY
                    </p>
                    <p
                      data-testid="purchase-quantity"
                      className={`bodySmBold ${styles.tranferredAmt}`}
                    >
                      {searchParams.get('numCubes')}
                    </p>
                  </div>
                  <div className={styles.summaryGroup}>
                    <p
                      className={`bodyMicroReg ${styles.purchaseSummaryLabel}`}
                    >
                      TOTAL
                    </p>
                    <p
                      data-testid="purchase-subtotal"
                      className={`bodySmBold ${styles.tranferredAmt}`}
                    >
                      {formatCurrencyUSD(subtotal)}
                    </p>
                  </div>

                  {isCreditCardPurchase && (
                    <>
                      <p
                        className={`bodySmBold ${styles.purchaseSummaryLabel} ${styles.fee}`}
                      >
                        Fees
                      </p>
                      <div className={styles.summaryGroup}>
                        <p
                          className={`bodyMicroReg ${styles.purchaseSummaryLabel}`}
                        >
                          PROCESSING FEE
                        </p>
                        <p className={`bodyMicroReg ${styles.processingFee}`}>
                          5%&nbsp;
                        </p>
                        <p
                          data-testid="purchase-fee"
                          className={`bodyMicroReg ${styles.processingFeeTotal}`}
                        >
                          ({formatCurrencyUSD(fee)})
                        </p>
                      </div>
                    </>
                  )}
                  <p
                    className={`${styles.purchaseTotalDue} heading4 ${styles.purchaseSummaryLabel}`}
                  >
                    Total Amount Due
                  </p>
                  <p
                    data-testid="purchase-total"
                    className={`order-total display4 ${styles.purchaseTotal}`}
                  >
                    {formatCurrencyUSD(total)}
                  </p>
                </div>
              </div>
              {stripe && elements && (
                <PaymentElement
                  onChange={(event) => {
                    if (event.complete) {
                      setIsPaymentFormComplete(true);
                    }
                  }}
                />
              )}
            </div>
            <div className={styles.buttonCTA}>
              <div className={styles.buttonCont}>
                <Button
                  id="backButton"
                  data-testid="confirm-purchase-back-button"
                  children="Back"
                  // size="large"
                  variant="secondary"
                  fullWidth="true"
                  onPress={() => {
                    if (isCreditCardPurchase) {
                      searchParams.delete('error');
                      navigate({
                        pathname: '/',
                        search: '?' + searchParams
                      });
                    } else {
                      navigate({
                        pathname: '/select-currency',
                        search: '?' + searchParams
                      });
                    }
                  }}
                />
              </div>
              <div className={styles.buttonCont}>
                <Button
                  type="submit"
                  id="completePurchaseButton"
                  data-testid="complete-purchase-button"
                  children="Complete Purchase"
                  variant="primary"
                  fullWidth="true"
                  isDisabled={
                    isPaymentLoading ||
                    makePurchase.isLoading ||
                    (!isPaymentFormComplete && isCreditCardPurchase)
                  }
                  isLoading={isPaymentLoading || makePurchase.isLoading}
                />
              </div>
            </div>
            {isCreditCardPurchase ? null : <TermsConditions />}
          </form>
        </main>
        <Footer />
      </div>
    </>
  );
}
export default ConfirmOrder;
